// Converted TSX version using TailwindCSS + Headless UI
// NOTE: This is a generated scaffold; adjust types and API shapes accordingly.

import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import axios from "../../util/axiosInstance";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

type ReportRow = {
  unitId: string;
  name: string;
  year: number;
  month: number;
  down: number;
  up: number;
  customer: number;
  precision: number;
  shutDowns: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  groupId?: string;
  groupName?: string;
};

export default function LastMonthReportDialog({
  open,
  onClose,
  groupId,
  groupName,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchReportData = async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${groupId}`
      );
      const unitsData = response.data || [];
      const unitIds = unitsData.map((u: any) => u.id);
      const nameMap: Record<string, string> = unitsData.reduce(
        (acc: any, u: any) => {
          acc[u.id] = u.name;
          return acc;
        },
        {}
      );

      if (unitIds.length === 0) {
        setError("No units found for this group.");
        return;
      }

      const payload = {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
        units: unitIds,
      };

      const reportResponse = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-month`,
        payload,
        {
          headers: { id: "anything", "Content-Type": "application/json" },
        }
      );

      const raw = reportResponse.data?.data?.mydict || {};
      const parsed: ReportRow[] = Object.entries(raw).map(
        ([unitId, values]: any) => ({
          unitId,
          name: nameMap[unitId] || unitId,
          ...values,
        })
      );

      setReportData(parsed);
    } catch (err) {
      console.error(err);
      setError("Failed to load report data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && groupId) fetchReportData();
  }, [open, groupId]);

  const filteredData = reportData.filter((r) =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const copyToClipboard = () => {
    if (filteredData.length === 0)
      return toast.warning("No report data available");

    const headers =
      "Unit Name\tYear\tMonth\tStop(Hrs)\tRun(Hrs)\tCustomer(Hrs)\tPRC(Hrs)\tShutdowns";
    const rows = filteredData
      .map(
        (item) =>
          `${item.name}\t${item.year}\t${item.month}\t${item.down}\t${item.up}\t${item.customer}\t${item.precision}\t${item.shutDowns}`
      )
      .join("\n");

    navigator.clipboard.writeText(`${headers}\n${rows}`);
    toast.success("Copied report to clipboard");
  };

  const exportToCSV = () => {
    if (filteredData.length === 0)
      return toast.warning("No report data available");

    const rows = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop(Hrs)",
        "Run(Hrs)",
        "Customer(Hrs)",
        "PRC(Hrs)",
        "Shutdowns",
      ],
      ...filteredData.map((item) => [
        item.name,
        item.year,
        item.month,
        item.down,
        item.up,
        item.customer,
        item.precision,
        item.shutDowns,
      ]),
    ];

    const csv = rows.map((r) => r.map((x) => `"${x}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Last_Monthly_Report.csv";
    link.click();

    toast.success("Exported CSV");
  };

  const exportToExcel = () => {
    if (filteredData.length === 0)
      return toast.warning("No report data available");

    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Stop(Hrs)",
      "Run(Hrs)",
      "Customer(Hrs)",
      "PRC(Hrs)",
      "Shutdowns",
    ];
    const rows = filteredData.map((item) => [
      item.name,
      item.year,
      item.month,
      item.down,
      item.up,
      item.customer,
      item.precision,
      item.shutDowns,
    ]);

    const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Monthly Report");
    XLSX.writeFile(workbook, "Last_Monthly_Report.xlsx");

    toast.success("Exported Excel");
  };

  const exportToPDF = () => {
    if (filteredData.length === 0)
      return toast.warning("No report data available");

    const doc = new jsPDF("l", "pt", "a4");
    doc.text("Monthly Units Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop(Hrs)",
        "Run(Hrs)",
        "Customer(Hrs)",
        "PRC(Hrs)",
        "Shutdowns",
      ],
    ];

    const rows = filteredData.map((item) => [
      item.name,
      item.year,
      new Date(item.year, item.month - 1).toLocaleString("default", {
        month: "long",
      }),
      item.down,
      item.up,
      item.customer,
      item.precision,
      item.shutDowns,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save("Monthly_Report.pdf");
    toast.success("Exported PDF");
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-50" />

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-5xl rounded bg-slate-800 p-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl text-white font-semibold">
                  {groupName
                    ? `Last Month Report - ${groupName}`
                    : "Last Month Report"}
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {/* Search + Export */}
              <div className="flex justify-between items-center mb-4">
                {/* LEFT — Search */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search unit..."
                  className="px-3 py-2 text-sm w-64 rounded bg-slate-700 text-white border border-gray-600"
                />

                {/* RIGHT — Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
                  >
                    Copy
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="px-3 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
                  >
                    CSV
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="px-3 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
                  >
                    Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="px-3 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
                  >
                    PDF
                  </button>
                </div>
              </div>

              {/* Table or feedback */}
              {loading ? (
                <div className="text-center text-white py-10">Loading...</div>
              ) : error ? (
                <div className="text-center text-red-400 py-4">{error}</div>
              ) : filteredData.length === 0 ? (
                <div className="text-center text-gray-300 py-4">
                  No report data available
                </div>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-700 rounded">
                  <table className="w-full text-sm text-left text-white">
                    <thead className="bg-slate-700 text-gray-200 sticky top-0">
                      <tr>
                        {[
                          "Unit Name",
                          "Year",
                          "Month",
                          "Stop(Hrs)",
                          "Run(Hrs)",
                          "Customer(Hrs)",
                          "PRC(Hrs)",
                          "Shutdowns",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-3 py-2 border-b border-gray-600"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((row, idx) => (
                        <tr
                          key={row.unitId}
                          className={
                            idx % 2 === 0 ? "bg-slate-800" : "bg-slate-700"
                          }
                        >
                          <td className="px-3 py-2">{row.name}</td>
                          <td className="px-3 py-2">{row.year}</td>
                          <td className="px-3 py-2">
                            {new Date(row.year, row.month - 1).toLocaleString(
                              "default",
                              { month: "long" }
                            )}
                          </td>
                          <td className="px-3 py-2">{row.down?.toFixed(1)}</td>
                          <td className="px-3 py-2">{row.up?.toFixed(1)}</td>
                          <td className="px-3 py-2">
                            {row.customer?.toFixed(1)}
                          </td>
                          <td className="px-3 py-2">
                            {row.precision?.toFixed(1)}
                          </td>
                          <td className="px-3 py-2">{row.shutDowns}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
